import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/lib/i18n";
import consent, {
  acceptAll,
  necessaryOnly,
  hasValidDecision,
  subscribe,
} from "@/lib/consent";

/**
 * Consent Foundation — first-visit banner (sticky bottom, non-modal).
 *
 * Behaviour rules (spec sections 3, 4, 11):
 *   * Renders only while there is no valid consent decision on record.
 *   * All three primary actions (Accept all, Necessary only, Settings)
 *     live on this first layer.
 *   * Accept all and Necessary only have equivalent visual prominence
 *     — same variant, same size, same colour weight.
 *   * The optional Analytics category is NEVER pre-ticked here; the
 *     banner never speaks on behalf of the user.
 *   * The banner is a `role="region"` labelled “Cookie consent”.  It
 *     is intentionally NOT a modal — the underlying site remains fully
 *     usable while a decision is pending, and there is no focus trap
 *     that could false-positively be interpreted as consent.
 *   * There is no close/X control on the first-visit banner — closing
 *     without a decision is explicitly forbidden as a dark pattern by
 *     the spec.  The user must pick one of the three real actions.
 *   * The site does NOT become blocked when the user chooses Necessary
 *     only.  The banner simply disappears.
 *
 * The banner subscribes to the centralized consent module so any
 * external mutation (dialog Save, footer button, tests) makes the
 * banner disappear without a page reload.
 */
export default function CookieConsentBanner({ onOpenSettings }) {
  const { t, locale } = useTranslation();
  const [visible, setVisible] = useState(() => !hasValidDecision());

  useEffect(() => {
    const unsub = subscribe(() => setVisible(!hasValidDecision()));
    return unsub;
  }, []);

  const handleAcceptAll = useCallback(() => {
    acceptAll({ locale });
  }, [locale]);

  const handleNecessaryOnly = useCallback(() => {
    necessaryOnly({ locale });
  }, [locale]);

  if (!visible) return null;

  const cookiesHref = locale === "de" ? "/de/cookies" : "/en/cookies";
  const privacyHref = locale === "de" ? "/de/datenschutz" : "/en/privacy";

  return (
    <div
      role="region"
      aria-label={t("consent.banner.title")}
      data-testid="cookie-consent-banner"
      className="fixed inset-x-0 bottom-0 z-[200] px-3 pb-3 sm:px-6 sm:pb-6"
    >
      <div
        className="mx-auto max-w-4xl rounded-2xl border border-border/60 bg-background/95 shadow-2xl backdrop-blur supports-[backdrop-filter]:bg-background/85"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.75rem)" }}
      >
        <div className="flex flex-col gap-4 p-5 sm:p-6">
          <div>
            <h2
              className="text-base font-semibold tracking-tight text-foreground"
              data-testid="cookie-consent-banner-title"
            >
              {t("consent.banner.title")}
            </h2>
            <p
              className="mt-2 text-sm leading-relaxed text-muted-foreground"
              data-testid="cookie-consent-banner-body"
            >
              {t("consent.banner.body")}
            </p>
            <p className="mt-3 text-xs text-muted-foreground">
              <Link
                to={cookiesHref}
                className="underline underline-offset-2 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 rounded"
                data-testid="cookie-consent-banner-link-cookies"
              >
                {t("consent.banner.link_cookies")}
              </Link>
              <span className="mx-2 text-muted-foreground/50">·</span>
              <Link
                to={privacyHref}
                className="underline underline-offset-2 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 rounded"
                data-testid="cookie-consent-banner-link-privacy"
              >
                {t("consent.banner.link_privacy")}
              </Link>
            </p>
          </div>

          {/* Three primary actions on the first layer.  Same variant
              and size so Accept and Reject have equivalent visual
              prominence per spec section 4. */}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="ghost"
              onClick={onOpenSettings}
              data-testid="cookie-consent-banner-settings"
            >
              {t("consent.banner.settings")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleNecessaryOnly}
              data-testid="cookie-consent-banner-necessary-only"
            >
              {t("consent.banner.necessary_only")}
            </Button>
            <Button
              type="button"
              onClick={handleAcceptAll}
              data-testid="cookie-consent-banner-accept-all"
            >
              {t("consent.banner.accept_all")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Re-export the underlying module for direct unit-testing consumers.
export { consent as _consent };
